import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd, { SITE_ORIGIN, SITE_FAVICON, SITE_NAME } from '@/components/feature/JsonLd';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

interface ArticlePageProps {
  slug: string;
}

export default function ArticlePage({ slug }: ArticlePageProps) {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const meta = articles.find((a) => a.slug === slug);
  const prefix = meta?.prefix ?? 'a1';

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const points = [`${prefix}.point1`, `${prefix}.point2`, `${prefix}.point3`, `${prefix}.point4`];
  const faqs = [
    { q: `${prefix}.faq1q`, a: `${prefix}.faq1a` },
    { q: `${prefix}.faq2q`, a: `${prefix}.faq2a` },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${prefix}.h1`),
    description: t(`${prefix}.metaDesc`),
    image: meta?.image,
    datePublished: '2026-08-23',
    dateModified: '2026-08-23',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: SITE_FAVICON },
    },
    mainEntityOfPage: `${SITE_ORIGIN}/${slug}`,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: t(f.q),
      acceptedAnswer: { '@type': 'Answer', text: t(f.a) },
    })),
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpenFaq(0);
  }, [slug]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">
              {t('article.breadcrumb')}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">{t(`${prefix}.cardTitle`)}</span>
          </nav>
        </div>

        {/* Header */}
        <header className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
            {t('nav.guides')}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
            {t(`${prefix}.h1`)}
          </h1>
          <p className="mt-4 text-xs text-foreground-500">
            {t('article.updated')}: 2026-08-23 · 5 {t('article.readingTime')}
          </p>
        </header>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-10">
          <div className="text-foreground-700 leading-relaxed text-sm md:text-base">
            <p>{t(`${prefix}.intro`)}</p>
          </div>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${prefix}.s1Title`)}</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t(`${prefix}.s1Body`)}</p>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${prefix}.s2Title`)}</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t(`${prefix}.s2Body`)}</p>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${prefix}.s3Title`)}</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t(`${prefix}.s3Body`)}</p>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article.pointsTitle')}</h2>
            <ul className="mt-5 space-y-3">
              {points.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  {t(item)}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article.faqTitle')}</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="font-medium text-sm md:text-base text-foreground-900">{t(f.q)}</span>
                    <i
                      className={`ri-add-line text-lg text-foreground-500 transition-transform ${
                        openFaq === i ? 'rotate-45' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === i && (
                    <p className="px-5 pb-5 text-sm md:text-base text-foreground-600 leading-relaxed">{t(f.a)}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article.categoriesTitle')}</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-700">{t('article.categoriesIntro')}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  {t(c.key)}
                  <i className="ri-arrow-right-up-line text-foreground-400"></i>
                </a>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t(`${prefix}.concl`)}</h2>
          </section>

          {/* Related articles */}
          <section className="pt-2">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              {t('articles.title')}
            </h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/${a.slug}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="h-20 w-full overflow-hidden rounded-md">
                    <img
                      src={a.image}
                      alt={t(`${a.prefix}.cardTitle`)}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-700 leading-snug">
                    {t(`${a.prefix}.cardTitle`)}
                  </h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
                    {t('articles.readMore')}
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </article>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
          <div className="rounded-lg bg-primary-500 p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">{t(`${prefix}.cta`)}</h2>
            <p className="mt-2 text-sm text-background-100">{t('article.ctaSub')}</p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-md bg-background-50 text-primary-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line text-base"></i>
              {t(`${prefix}.cta`)}
            </a>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}